import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import * as mapbox from 'mapbox-gl';
import { filter, take } from 'rxjs/operators';
import { DriverDetail, RecommendedShiftRegion } from 'src/app/types';

@Component({
  selector: 'lohi-shift-map',
  templateUrl: './shift-map.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShiftMapComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChildren('map') private mapElements: QueryList<ElementRef<HTMLDivElement>>;
  private map: mapbox.Map;
  @Input() public regions: RecommendedShiftRegion[];
  @Input() public drivers: DriverDetail[];
  @Input() public selectedRegionId: number;
  @Input() public selectedDriver: DriverDetail | null;
  @Output() public regionSelected = new EventEmitter<number>();

  constructor() {
    (mapbox as any).accessToken =
      'pk.eyJ1Ijoic2F0ZXN0ZGV2IiwiYSI6ImNrYmVraDllZDBtcXQycHFlbzVqYXk0cnMifQ.HzVySDvy0mLDW03ykD2HNw';
  }

  public ngOnInit(): void {}

  public ngAfterViewInit(): void {
    this.initMap();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.recalculateMap();
  }

  private async initMap() {
    // Make sure we have a map element
    if (this.mapElements.length === 0) {
      await this.mapElements.changes
        .pipe(
          filter((changes: QueryList<any>) => {
            return changes.length > 0;
          }),
          take(1),
        )
        .toPromise();
    }

    const options: mapbox.MapboxOptions = {
      container: this.mapElements.first.nativeElement,
      style: 'mapbox://styles/mapbox/streets-v11',
    };

    await new Promise((resolve) => setTimeout(resolve, 100));

    this.map = new mapbox.Map(options);
    this.map.setZoom(3);
    this.map.setCenter({ lat: 39.8283, lng: -98.5795 });
    this.map.addControl(new mapbox.NavigationControl());
    this.map.on('click', 'regions', (e) => {
      if (e.features[0]?.properties?.id) {
        this.regionSelected.next(e.features[0]?.properties?.id);
      }
    });
    this.recalculateMap();
  }

  private recalculateMap() {
    if (this.regions && this.map) {
      const regions = this.regions.filter((region) => {
        if (this.selectedRegionId) {
          return region.recommendedShiftId === this.selectedRegionId;
        }
        return true;
      });
      const bounds = new mapbox.LngLatBounds();
      (regions || []).forEach((region) => {
        bounds.extend([region.startLongitude, region.startLatitude]);
      });
      (this.drivers || []).forEach((driver) => {
        if (driver.lastLocation) {
          bounds.extend([driver.lastLocation.longitude, driver.lastLocation.latitude]);
        }
      });
      if (!bounds.isEmpty()) {
        this.map.setMaxZoom(8);
        this.map.fitBounds(bounds, { padding: 200 });
        this.map.setMaxZoom(15);
        if (this.map.getLayer('regions')) {
          // just a repaint
          (this.map.getSource('regions') as mapbox.GeoJSONSource).setData(getRegionsSource(regions).data);
        } else {
          this.map.addSource('regions', getRegionsSource(regions));
          this.map.addLayer({
            id: `regions`,
            type: 'circle',
            source: `regions`,
            paint: {
              'circle-radius': ['interpolate', ['exponential', 2], ['zoom'], 0, 0, 20, ['*', ['get', 'radius'], 1]],
              'circle-color': 'rgba(128, 128, 128, .5)',
            },
          });
          this.map.addLayer({
            id: 'regions-labels',
            type: 'symbol',
            source: 'regions',
            layout: {
              'text-field': ['get', 'name'],
              'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
              'text-radial-offset': 0.5,
              'text-justify': 'center',
            },
          });
        }
        if (this.map.getLayer('drivers')) {
          this.map.removeLayer('drivers').removeLayer('drivers-labels').removeSource('drivers');
        }

        if (this.map.getLayer('selected-driver')) {
          this.map.removeLayer('selected-driver').removeSource('selected-driver');
        }

        if (this.selectedDriver) {
          this.map.setZoom(12);
          this.map.setCenter({
            lat: this.selectedDriver.lastLocation.latitude,
            lng: this.selectedDriver.lastLocation.longitude,
          });
        }

        if (this.drivers && this.drivers.length) {
          this.map.addSource('drivers', getDriverSource(this.drivers));
          this.map.addLayer({
            id: `drivers`,
            type: 'circle',
            source: `drivers`,
            paint: {
              'circle-radius': 10,
              'circle-color': 'rgb(68, 138, 255)',
            },
          });
          this.map.addSource('selected-driver', getDriverSource([this.selectedDriver]));
          this.map.addLayer({
            id: `selected-driver`,
            type: 'circle',
            source: `selected-driver`,
            paint: {
              'circle-radius': 10,
              'circle-color': 'rgb(95, 154, 126)',
            },
          });
          this.map.addLayer({
            id: 'drivers-labels',
            type: 'symbol',
            source: 'drivers',
            layout: {
              'text-field': ['get', 'name'],
              'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
              'text-radial-offset': 0.5,
              'text-justify': 'center',
            },
          });
        }
      }
    }
  }
}

function getRegionsSource(regions: RecommendedShiftRegion[]): mapboxgl.GeoJSONSourceRaw {
  const fracData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
    type: 'FeatureCollection',
    features: regions.map((region) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [region.startLongitude, region.startLatitude],
      },
      properties: {
        id: region.recommendedShiftId,
        name: region.name,
        radius: metersToPixelsAtMaxZoom(region.startRadiusMeters, region.startLatitude),
      },
    })),
  };
  const fracSource: mapboxgl.GeoJSONSourceRaw = {
    type: 'geojson',
    data: fracData,
  };

  return fracSource;
}

function getDriverSource(drivers: DriverDetail[]): mapboxgl.GeoJSONSourceRaw {
  if (!drivers || !drivers.length) {
    return null;
  }
  const fracData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
    type: 'FeatureCollection',
    features: drivers
      .filter((d) => !!d?.lastLocation)
      .map((driver) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [driver.lastLocation.longitude, driver.lastLocation.latitude],
        },
        properties: {
          id: driver.id,
          name: driver.driverName,
        },
      })),
  };
  const fracSource: mapboxgl.GeoJSONSourceRaw = {
    type: 'geojson',
    data: fracData,
  };

  return fracSource;
}

// From https://stackoverflow.com/questions/37599561/drawing-a-circle-with-the-radius-in-miles-meters-with-mapbox-gl-js
function metersToPixelsAtMaxZoom(meters: number, latitude: number) {
  return meters / (78271.484 / 2 ** 20) / Math.cos((latitude * Math.PI) / 180);
}
